import { useMemo } from "react";
import { Funnel } from "@b2bportal/core-types";
import { Experiments } from "./experimentsList";
import { useExperiment } from "./useExperiment";

/**
 * Maps funnels to their corresponding experiment flags
 * Returns whether each funnel is enabled based on LaunchDarkly experiments
 * While loading, defaults to enabled to prevent premature redirects
 * If LaunchDarkly fails, the API layer returns fallback experiment data
 */
export const useFunnelEnabled = () => {
  const { isExperimentVariant, isLoading } = useExperiment();

  const isFlightsEnabled = useMemo(
    () =>
      isLoading ||
      isExperimentVariant(
        Experiments.FLIGHTS_ENABLED.name,
        Experiments.FLIGHTS_ENABLED.variants.available
      ),
    [isExperimentVariant, isLoading]
  );

  const isStaysEnabled = useMemo(
    () =>
      isLoading ||
      isExperimentVariant(
        Experiments.STAYS_ENABLED.name,
        Experiments.STAYS_ENABLED.variants.available
      ),
    [isExperimentVariant, isLoading]
  );

  const isCarsEnabled = useMemo(
    () =>
      isLoading ||
      isExperimentVariant(
        Experiments.CARS_ENABLED.name,
        Experiments.CARS_ENABLED.variants.available
      ),
    [isExperimentVariant, isLoading]
  );

  const isActivitiesEnabled = useMemo(
    () =>
      isLoading ||
      isExperimentVariant(
        Experiments.ACTIVITIES_ENABLED.name,
        Experiments.ACTIVITIES_ENABLED.variants.available
      ),
    [isExperimentVariant, isLoading]
  );

  const isPackagesEnabled = useMemo(
    () =>
      isLoading ||
      isExperimentVariant(
        Experiments.PACKAGES_ENABLED.name,
        Experiments.PACKAGES_ENABLED.variants.available
      ),
    [isExperimentVariant, isLoading]
  );

  const isTripsEnabled = useMemo(
    () =>
      isLoading ||
      isExperimentVariant(
        Experiments.TRIPS_ENABLED.name,
        Experiments.TRIPS_ENABLED.variants.available
      ),
    [isExperimentVariant, isLoading]
  );

  const defaultFunnel = useMemo((): Funnel => {
    // While loading, default to Lodging to avoid rendering issues
    if (isLoading) return Funnel.Lodging;

    // Return the first enabled funnel in priority order (excluding Trips since it has no homepage)
    if (isStaysEnabled) return Funnel.Lodging;
    if (isFlightsEnabled) return Funnel.Air;
    if (isCarsEnabled) return Funnel.Ground;
    if (isActivitiesEnabled) return Funnel.Activities;
    if (isPackagesEnabled) return Funnel.Package;
    // Fallback to Lodging even if disabled to prevent crashes
    return Funnel.Lodging;
  }, [
    isLoading,
    isStaysEnabled,
    isFlightsEnabled,
    isCarsEnabled,
    isActivitiesEnabled,
    isPackagesEnabled,
  ]);

  return {
    isFlightsEnabled,
    isStaysEnabled,
    isCarsEnabled,
    isActivitiesEnabled,
    isPackagesEnabled,
    isTripsEnabled,
    defaultFunnel,
    isLoading,
  };
};
